import { useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, ChevronLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useApprovalQueue, useOrder, useRejectOrder, useSendSupplier, useTrackingOptions } from '../hooks/useStockOrders';
import { useCurrentUser } from '../hooks/useCurrentUser';

const money = (v) => `£${Number(v || 0).toFixed(2)}`;

// Only these two roles may be named as the PO requester in ApprovalMax.
const REQUESTERS = ['Gavin Esterhuizen', 'Phillip Hall'];

function OrderDetail({ orderId, onBack }) {
  const { data, isLoading } = useOrder(orderId);
  const reject = useRejectOrder();
  const send = useSendSupplier();
  const { data: tracking } = useTrackingOptions();
  const { data: me } = useCurrentUser();
  const [requester, setRequester] = useState('');
  const [contract, setContract] = useState('');
  const [area, setArea] = useState('');
  const [poComments, setPoComments] = useState({});
  const [choice, setChoice] = useState({});
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [flash, setFlash] = useState(null);
  const [sendingSupplier, setSendingSupplier] = useState(null);

  useEffect(() => {
    if (!data?.lines) return;
    const init = {};
    for (const l of data.lines) {
      const pref = l.available_suppliers.find((s) => s.is_preferred) || l.available_suppliers[0];
      if (pref) init[l.id] = pref.supplier_id;
    }
    setChoice(init);
  }, [data]);

  useEffect(() => {
    if (me?.display_name && REQUESTERS.includes(me.display_name)) setRequester((prev) => prev || me.display_name);
  }, [me]);

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const pos = useMemo(() => {
    if (!data?.lines) return [];
    const readOnly = data.order.status !== 'submitted';
    const map = new Map();
    for (const l of data.lines) {
      const sid = readOnly ? l.supplier_id : choice[l.id];
      if (!sid) continue;
      const sup = l.available_suppliers.find((s) => s.supplier_id === sid);
      const unit = readOnly ? l.unit_price : sup?.unit_price;
      if (!map.has(sid)) map.set(sid, { supplier_id: sid, supplier_name: sup?.supplier_name || `Supplier ${sid}`, lines: [], subtotal: 0 });
      const grp = map.get(sid);
      grp.lines.push({ id: l.id, name: l.name, quantity: l.quantity, unit });
      grp.subtotal += Number(unit || 0) * l.quantity;
    }
    return Array.from(map.values());
  }, [data, choice]);

  if (isLoading || !data) return <div className="p-6 text-sm text-gray-400">Loading…</div>;
  const { order, lines, purchase_orders: generated } = data;
  const readOnly = order.status !== 'submitted';
  const grandTotal = pos.reduce((t, p) => t + p.subtotal, 0);
  const sentFor = (supplierId) => (generated || []).find((g) => g.supplier_id === supplierId && ['sent', 'approved'].includes(g.status));

  const sendSupplier = async (po) => {
    setSendingSupplier(po.supplier_id); setFlash(null);
    try {
      const r = await send.mutateAsync({ id: order.id, supplier_id: po.supplier_id, line_ids: po.lines.map((l) => l.id), requester, contract, area, comments: poComments[po.supplier_id] || undefined });
      setFlash({ ok: true, msg: `${po.supplier_name}: sent to ApprovalMax (${r.po_number}).` });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.error || `Could not send ${po.supplier_name}.` });
    } finally { setSendingSupplier(null); }
  };
  const doReject = async () => { await reject.mutateAsync({ id: order.id, reason }); onBack(); };

  return (
    <div>
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to queue
      </button>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{order.reference || `Order #${order.id}`}</h2>
          <p className="text-sm text-gray-500">Raised by {order.requested_by_name} · deliver to {order.delivery_location || 'no location'}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${order.status === 'submitted' ? 'bg-amber-100 text-amber-800' : order.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>{order.status}</span>
      </div>
      {order.notes && <p className="mb-4 rounded bg-gray-50 px-3 py-2 text-sm text-gray-600">{order.notes}</p>}
      {flash && <div className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${flash.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>{flash.msg}</div>}

      {!readOnly && (
        <div className="mb-6">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">Assign supplier per item</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-2.5">Item</th><th className="px-4 py-2.5 text-right">Qty</th><th className="px-4 py-2.5">Supplier</th><th className="px-4 py-2.5 text-right">Unit price</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((l) => {
                  const sid = choice[l.id];
                  const sup = l.available_suppliers.find((s) => s.supplier_id === sid);
                  const locked = !!sentFor(sid);
                  return (
                    <tr key={l.id}>
                      <td className="px-4 py-2.5 text-gray-800">{l.name}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{l.quantity}</td>
                      <td className="px-4 py-2.5">
                        <select value={sid || ''} disabled={locked} onChange={(e) => setChoice((c) => ({ ...c, [l.id]: Number(e.target.value) }))} className="rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100">
                          {l.available_suppliers.length === 0 && <option value="">no supplier linked</option>}
                          {l.available_suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name} — {money(s.unit_price)}{s.is_preferred ? ' ★' : ''}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{money(sup?.unit_price)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-400">PO details — applied to every purchase order below</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Requester</span>
              <select value={requester} onChange={(e) => setRequester(e.target.value)} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm">
                <option value="">— select requester —</option>
                {REQUESTERS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Contract</span>
              <select value={contract} onChange={(e) => setContract(e.target.value)} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm">
                <option value="">— none —</option>
                {(tracking?.contracts || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Area</span>
              <select value={area} onChange={(e) => setArea(e.target.value)} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm">
                <option value="">— none —</option>
                {(tracking?.areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
          </div>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-400">{readOnly ? 'Purchase orders' : `${pos.length} purchase order${pos.length === 1 ? '' : 's'} — send each to ApprovalMax`}</p>
        <p className="text-sm text-gray-500">Total across all POs: <span className="font-bold text-gray-900">{money(grandTotal)}</span></p>
      </div>

      <div className="space-y-4">
        {pos.map((po) => {
          const gen = sentFor(po.supplier_id);
          const isSending = sendingSupplier === po.supplier_id;
          return (
            <div key={po.supplier_id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <div>
                  <span className="text-xs uppercase tracking-wide text-gray-400">Supplier</span>
                  <div className="text-base font-semibold text-gray-900">{po.supplier_name}</div>
                  {gen?.po_number && <div className="font-mono text-xs text-gray-500">{gen.po_number}</div>}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right"><span className="block text-xs uppercase tracking-wide text-gray-400">Date</span><span className="text-sm text-gray-700">{today}</span></div>
                  {gen ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-3 py-1.5 text-sm font-semibold text-emerald-800"><CheckCircleIcon className="h-4 w-4" /> Sent to ApprovalMax</span>
                  ) : !readOnly ? (
                    <button type="button" onClick={() => sendSupplier(po)} disabled={isSending || send.isPending || !requester} title={!requester ? "Select a requester first" : undefined} className="inline-flex items-center gap-1 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">
                      <PaperAirplaneIcon className="h-4 w-4" /> {isSending ? 'Sending…' : 'Send to ApprovalMax'}
                    </button>
                  ) : null}
                </div>
              </div>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <tr><th className="px-5 py-2">Item</th><th className="px-5 py-2 text-right">Qty</th><th className="px-5 py-2 text-right">Unit price</th><th className="px-5 py-2 text-right">Amount</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {po.lines.map((l) => (
                    <tr key={l.id}>
                      <td className="px-5 py-2 text-gray-800">{l.name}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{l.quantity}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-gray-600">{money(l.unit)}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-gray-900">{money(Number(l.unit || 0) * l.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end border-t border-gray-200 px-5 py-2.5 text-sm"><span className="text-gray-500">Total&nbsp;&nbsp;</span><span className="w-28 text-right font-bold tabular-nums text-gray-900">{money(po.subtotal)}</span></div>
              {!readOnly && !gen && (
                <div className="border-t border-gray-200 px-5 py-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-500">Comment for this PO <span className="font-normal text-gray-400">(added as a note line on {po.supplier_name}&apos;s PO)</span></span>
                    <textarea value={poComments[po.supplier_id] || ''} onChange={(e) => setPoComments((m) => ({ ...m, [po.supplier_id]: e.target.value }))} rows={2} placeholder="Optional note for this supplier…" className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                  </label>
                </div>
              )}
            </div>
          );
        })}
        {pos.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-400">Assign suppliers to preview the purchase orders.</p>}
      </div>

      {!readOnly && (
        <div className="mt-5">
          {!rejecting ? (
            <button type="button" onClick={() => setRejecting(true)} className="rounded-md border border-rose-300 px-4 py-2 text-sm font-medium text-rose-700"><XCircleIcon className="mr-1 inline h-4 w-4" />Reject order</button>
          ) : (
            <div className="max-w-md space-y-2">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button type="button" onClick={doReject} disabled={reject.isPending} className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirm reject</button>
                <button type="button" onClick={() => setRejecting(false)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProcurementApprovals() {
  const { data: queue } = useApprovalQueue('submitted');
  const [selected, setSelected] = useState(null);

  return (
    <div className="p-6">
      {selected ? (
        <OrderDetail orderId={selected} onBack={() => setSelected(null)} />
      ) : (
        <>
          <h1 className="text-2xl font-bold text-gray-900">Procurement Approvals</h1>
          <p className="mt-1 text-sm text-gray-500">Review submitted stock orders, assign a supplier to each item, and send each supplier’s purchase order to ApprovalMax.</p>
          <h2 className="mb-2 mt-5 text-sm font-bold uppercase tracking-wide text-gray-400">Awaiting approval ({queue?.length || 0})</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-2.5">Reference</th><th className="px-4 py-2.5">Raised by</th><th className="px-4 py-2.5">Deliver to</th><th className="px-4 py-2.5 text-right">Items</th><th className="px-4 py-2.5">Submitted</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(queue || []).map((o) => (
                  <tr key={o.id} onClick={() => setSelected(o.id)} className="cursor-pointer hover:bg-brand-light/40">
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{o.reference}</td>
                    <td className="px-4 py-2.5 text-gray-800">{o.requested_by_name}</td>
                    <td className="px-4 py-2.5 text-gray-600">{o.delivery_location || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{o.line_count}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">{o.submitted_at ? new Date(o.submitted_at).toLocaleDateString('en-GB') : ''}</td>
                  </tr>
                ))}
                {queue && queue.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Nothing awaiting approval.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default ProcurementApprovals;
